import { Painter } from '@/gfx/pixel';
import { C } from '@/gfx/palette';

export type ItemKind =
  | 'battery' | 'bottle' | 'firecracker' | 'camera' | 'map' | 'crowbar' | 'fuse'
  | 'key_kitchen' | 'key_east' | 'key_third' | 'key_master' | 'key_cellar'
  | 'page1' | 'page2' | 'page3' | 'note';

export interface ItemDef {
  id: ItemKind;
  name: string;
  desc: string;
  /** stackable count */
  stack: boolean;
  /** usable with Space */
  use?: 'throw' | 'battery' | 'flash' | 'read';
  key?: boolean;
  icon: () => HTMLCanvasElement;
}

const O = C.outline;
function keyIcon(color: string): HTMLCanvasElement {
  const p = new Painter(16, 16);
  p.art(0, 0, [
    '....OOOO........',
    '...OKKKKO.......',
    '..OKKOOKKO......',
    '..OKKOOKKO......',
    '...OKKKKO.......',
    '....OKKO........',
    '....OKKO........',
    '....OKKOO.......',
    '....OKKKKO......',
    '....OKKOOO......',
    '....OKKOO.......',
    '....OKKKKO......',
    '.....OOOOO......',
  ], { O, K: color });
  return p.done();
}

export const ITEMS: Record<ItemKind, ItemDef> = {
  battery: {
    id: 'battery', name: '배터리', desc: '손전등 배터리를 50% 충전한다.', stack: true, use: 'battery',
    icon: () => {
      const p = new Painter(16, 16);
      p.panel(4, 2, 8, 13, '#2f2f3a', O, '#4a4a58', '#1c1c24');
      p.rect(6, 0, 4, 2, C.metalLt).rect(5, 5, 6, 3, '#c8433d').rect(5, 9, 6, 4, '#4f9d5a');
      return p.done();
    },
  },
  bottle: {
    id: 'bottle', name: '유리병', desc: '던지면 깨지는 소리로 괴물을 유인한다.', stack: true, use: 'throw',
    icon: () => {
      const p = new Painter(16, 16);
      p.art(0, 0, [
        '......OOOO......', '......OGGO......', '......OGGO......', '.....OOGGOO.....', '....OGGGGGGO....', '....OGGLGGGO....',
        '....OGGLGGGO....', '....OGGGGGGO....', '....OGGGGGGO....', '....OGGGGGGO....', '....OGGGGGGO....', '.....OOOOOO.....',
      ], { O, G: '#4f8f6a', L: '#a3d9b8' });
      return p.done();
    },
  },
  firecracker: {
    id: 'firecracker', name: '폭죽', desc: '던지면 잠시 후 터진다. 가려지지 않은 가까운 괴물은 멈추고 친구를 놓친다.', stack: true, use: 'throw',
    icon: () => {
      const p = new Painter(16, 16);
      p.panel(4, 5, 8, 10, '#c8433d', O, '#e06a60', '#95302c');
      p.rect(5, 8, 6, 2, '#e2b53c');
      p.rect(7, 2, 2, 3, C.metalDk).px(8, 1, C.fire).px(7, 0, C.fireHi);
      return p.done();
    },
  },
  camera: {
    id: 'camera', name: '카메라', desc: '앞쪽 가까이에 보이는 괴물을 잠시 기절시킨다. 필름이 제한되어 있다.', stack: true, use: 'flash',
    icon: () => {
      const p = new Painter(16, 16);
      p.panel(1, 4, 14, 10, '#2a2a33', O, '#4a4a58', '#1c1c24');
      p.rect(5, 6, 6, 6, C.metalLt).rect(6, 7, 4, 4, '#3a5a9a').px(7, 8, '#c4e0ee');
      p.rect(3, 2, 4, 2, C.metalDk).rect(12, 6, 2, 2, C.fire);
      return p.done();
    },
  },
  map: {
    id: 'map', name: '저택 도면', desc: '수첩에 보관한다. 지도(M)에 저택의 전체 방 배치가 표시된다.', stack: false,
    icon: () => {
      const p = new Painter(16, 16);
      p.rect(2, 2, 12, 12, C.paper).box(2, 2, 12, 12, C.paperDk);
      p.box(4, 4, 4, 4, C.ink).box(8, 4, 4, 3, C.ink).box(4, 9, 8, 3, C.ink);
      p.px(10, 10, '#c8433d');
      return p.done();
    },
  },
  crowbar: {
    id: 'crowbar', name: '쇠지레', desc: '판자로 막힌 문을 뜯어낼 수 있다.', stack: false,
    icon: () => {
      const p = new Painter(16, 16);
      p.art(0, 0, [
        '..OOO...........', '.OMMMO..........', '.OMOMMO.........', '.OO.OMMO........', '.....OMMO.......', '......OMMO......',
        '.......OMMO.....', '........OMMO....', '.........OMMO...', '..........OMMO..', '...........OMMO.', '............OOO.',
      ], { O, M: C.metal });
      return p.done();
    },
  },
  fuse: {
    id: 'fuse', name: '퓨즈', desc: '두꺼비집에 끼우면 전기가 들어온다.', stack: false,
    icon: () => {
      const p = new Painter(16, 16);
      p.rect(4, 3, 8, 10, '#d9d2c1').box(4, 3, 8, 10, O);
      p.rect(6, 1, 4, 2, C.metal).rect(6, 13, 4, 2, C.metal);
      p.rect(7, 5, 2, 6, '#c8433d');
      return p.done();
    },
  },
  key_kitchen: { id: 'key_kitchen', name: '주방 열쇠', desc: '식당에서 주방으로 가는 문의 열쇠.', stack: false, key: true, icon: () => keyIcon(C.brass) },
  key_east: { id: 'key_east', name: '동쪽 날개 열쇠', desc: '2층 동쪽 날개 문의 열쇠.', stack: false, key: true, icon: () => keyIcon('#8fb4c8') },
  key_third: { id: 'key_third', name: '3층 열쇠', desc: '3층으로 올라가는 계단 문의 열쇠.', stack: false, key: true, icon: () => keyIcon('#c8433d') },
  key_cellar: { id: 'key_cellar', name: '지하실 열쇠', desc: '지하 통로 철문의 열쇠.', stack: false, key: true, icon: () => keyIcon('#7f8590') },
  key_master: { id: 'key_master', name: '정문 열쇠', desc: '저택 정문을 여는 커다란 열쇠.', stack: false, key: true, icon: () => keyIcon('#e2b53c') },
  page1: { id: 'page1', name: '일기 첫 장', desc: '수첩(J)에 옮겨 적었다.', stack: false, use: 'read', icon: () => pageIcon() },
  page2: { id: 'page2', name: '일기 둘째 장', desc: '수첩(J)에 옮겨 적었다.', stack: false, use: 'read', icon: () => pageIcon() },
  page3: { id: 'page3', name: '일기 셋째 장', desc: '수첩(J)에 옮겨 적었다.', stack: false, use: 'read', icon: () => pageIcon() },
  note: { id: 'note', name: '쪽지', desc: '수첩(J)에 옮겨 적었다.', stack: false, use: 'read', icon: () => pageIcon() },
};

function pageIcon(): HTMLCanvasElement {
  const p = new Painter(16, 16);
  p.rect(3, 1, 10, 14, C.paper).box(3, 1, 10, 14, C.paperDk);
  p.hline(5, 4, 6, C.ink).hline(5, 7, 5, C.ink).hline(5, 10, 6, C.ink);
  return p.done();
}

const iconCache = new Map<ItemKind, HTMLCanvasElement>();
export function itemIcon(kind: ItemKind): HTMLCanvasElement {
  let c = iconCache.get(kind);
  if (!c) {
    c = ITEMS[kind].icon();
    iconCache.set(kind, c);
  }
  return c;
}

export interface InvItem {
  kind: ItemKind;
  count: number;
  /** note/page text id */
  text?: string;
}
